// src/lib/dealSidebarDisplay.ts
// Phase-4 Runde 2a (Seitenleiste "Meine Deals", AppShell.tsx): reine Auflösungslogik
// "gespeicherter Deal -> Score/Urteil/Ort", ausgelagert aus der Komponente, damit sie ohne
// React/DOM direkt testbar ist (Dominiks Vorgabe, Nachtest 15.10.2026, Punkt 2e: der
// Sidebar-Score muss im Paritätstest stecken, dieselbe Zahl wie Akte/"Meine Deals").
//
// Nutzt dieselbe geteilte Berechnung wie DealDetail.tsx/Check.tsx
// (computeDealResultFromSnapshot()) -- kein zweiter Rechenweg, also zwangsläufig dieselbe
// Zahl bei gleichem Snapshot. Dieser Datei-Export macht das nur EXPLIZIT nachweisbar.
import { gewaehlterOrt } from "./dealNaming.js";
import type { AnalyzerType, PortfolioObject } from "../hooks/usePortfolio";
import { resolveStandort, lageInputFromStandort } from "../hooks/useStandort.js";
import { computeDealResultFromSnapshot } from "./dealResultFromSnapshot.js";
import { withRawPurchaseCostFields } from "./purchaseCosts.js";
import { resolveEtwSnapshot } from "./etwSnapshot.js";
import { resolveMfhSnapshot } from "./mfhSnapshot.js";
import { resolveEfhSnapshot } from "./efhSnapshot.js";
import { resolveGewerbeSnapshot } from "./gewerbeSnapshot.js";
import { resolveMixedSnapshot } from "./mixedSnapshot.js";
import type { DealUrteil } from "../core/score/dealScore";

export type DealDisplayInfo = {
  score: number;
  urteil: DealUrteil;
  ort: string | null;
  /** Nachtrag Phase-4 Runde 2a (Karten in "Meine Deals", Portfolio.tsx): dieselbe
   *  Rechnung wie Score/Urteil -- computeDealResultFromSnapshot() liefert sie ohnehin mit,
   *  kein zweiter Rechenweg fuer die Kartenkennzahlen. */
  cashflowPerMonth: number;
  bruttorenditePct: number;
  /** Nachtrag 15.10.2026 (Seitenleiste, zweite Zeile "Kaufpreis · PLZ"): bei ETW lebt die
   *  PLZ im Snapshot-Input, nicht auf Zeilenebene (siehe deal.plz unten) -- diese bereits
   *  intern aufgeloeste PLZ hier mit auszuliefern spart der Seitenleiste eine zweite,
   *  objektart-abhaengige Fallunterscheidung. null, wenn keine (vollstaendige) PLZ vorliegt. */
  plz: string | null;
};

export function resolveAnalyzerSnapshot(oa: AnalyzerType, raw: unknown) {
  switch (oa) {
    case "mfh": return resolveMfhSnapshot(raw);
    case "efh": return resolveEfhSnapshot(raw);
    case "gewerbe": return resolveGewerbeSnapshot(raw);
    case "mixeduse": return resolveMixedSnapshot(raw);
    default: return resolveEtwSnapshot(raw);
  }
}

/**
 * Score/Urteil/Ort fuer EINEN Deal in der Sidebar-Liste -- `standortData`/`plzBundesland`
 * sind die bereits geladenen JSON-Inhalte (siehe loadData()/loadPlzBundesland() in
 * useStandort.ts). null, wenn der Snapshot unvollstaendig ist oder etwas fehlschlaegt
 * (Sidebar zeigt dann nur den Titel, keinen Score -- kein erfundener Wert).
 */
export function resolveDealDisplayInfo(
  deal: Pick<PortfolioObject, "analyzer_type" | "data" | "plz">,
  standortData: Parameters<typeof resolveStandort>[1],
  plzBundesland: Parameters<typeof resolveStandort>[2]
): DealDisplayInfo | null {
  try {
    const oa = deal.analyzer_type;
    const raw = (deal.data as Record<string, unknown> | undefined)?.snapshot;
    const resolved = resolveAnalyzerSnapshot(oa, raw);
    if (!resolved.complete) return null;
    const rawInput = (raw as Record<string, unknown> | undefined)?.input;
    const mergedInput = withRawPurchaseCostFields(resolved.input as Record<string, unknown>, rawInput);
    const plz = oa === "etw" ? (mergedInput as any).plz : (deal.plz ?? "");
    const plzStr = typeof plz === "string" ? plz : "";
    const plzComplete = /^\d{5}$/.test(plzStr);
    const standort = plzComplete
      ? resolveStandort(plzStr, standortData, plzBundesland)
      : { info: null, notFound: false, cityFallback: null, bundesland: null, bundeslandOptions: null, ort: null };
    const lage = lageInputFromStandort(standort, plzComplete);
    const result = computeDealResultFromSnapshot(oa, mergedInput, resolved.playground, lage);
    return {
      score: result.score.score,
      urteil: result.score.urteil,
      ort: gewaehlterOrt(deal.data) ?? standort.info?.stadt ?? standort.cityFallback?.stadt ?? standort.ort ?? null,
      cashflowPerMonth: result.cashflowPerMonth,
      bruttorenditePct: result.bruttorenditePct,
      plz: plzComplete ? plzStr : null,
    };
  } catch {
    return null;
  }
}
